import { useState, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  Logout,
  HistoryTwoTone as HistoryIcon,
  SourceTwoTone as SourceIcon,
  Home as HomeIcon,
  CalendarMonth as CalendarIcon,
  ScienceTwoTone as TrialsIcon,
  Person as PatientIcon,
  LocalHospitalTwoTone as ClinicIcon,
  GroupsTwoTone as StaffIcon,
  AssessmentTwoTone as ReportGenerationIcon,
  QuestionAnswerTwoTone as SurveyIcon,
  NotificationsNone as NotificationIcon,
  HeadsetMicTwoTone as SupportIcon,
  SupervisorAccountTwoTone as ViewAsIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { USER_ROLES, INV_RELATED_ROLES } from "../../utils/constants";

import logo from "../../assets/img/smartTrail.svg";
import logUserAction from "../../apis/userLogger";
import useUser from "../../hooks/user";
import useGetNotificationUnreadCount from "../../hooks/useGetNotificationUnreadCount";
import useGetStaffNotifications from "../../hooks/useGetStaffNotifications";
import useMarkNotificationReadMutation from "../../hooks/useMarkNotificationReadMutation";
import useMarkAllNotificationsReadMutation from "../../hooks/useMarkAllNotificationsReadMutation";
import useGetSupportUnreadCount from "../../hooks/useGetSupportUnreadCount";
import ViewDashboardAsModal from "../Modals/ViewDashboardAsModal";
import ImpersonationBanner from "../ImpersonationBanner";

dayjs.extend(relativeTime);

const NavBar = memo(({ active, setAddStaff }) => {
  const user = useUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const open = Boolean(anchorEl);
  const notifOpen = Boolean(notifAnchorEl);
  const handleClick = (event) => {
    logUserAction("Click On Settings");
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const user_data = JSON.parse(localStorage.getItem("user_data"));

  const { data: unreadData } = useGetNotificationUnreadCount();
  const { data: supportUnreadData } = useGetSupportUnreadCount({
    enabled:
      user_data?.role === USER_ROLES.SUPER_ADMIN || INV_RELATED_ROLES.includes(user_data?.role),
  });
  const supportUnreadCount = supportUnreadData?.count ?? 0;
  const {
    data: notificationsData,
    isLoading: notifLoading,
    refetch: refetchNotifications,
  } = useGetStaffNotifications({
    enabled: notifOpen,
  });
  const { mutate: markRead } = useMarkNotificationReadMutation();
  const { mutate: markAllRead, isPending: markingAll } = useMarkAllNotificationsReadMutation();

  const unreadCount = unreadData?.count ?? 0;
  const notifications = notificationsData?.results ?? [];

  const handleNotifOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
    refetchNotifications();
  };
  const handleNotifClose = () => setNotifAnchorEl(null);

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) markRead(notification.id);
    if (notification.link) navigate(notification.link);
    handleNotifClose();
  };

  const handleStaffClick = () => {
    if (setAddStaff) setAddStaff(false);
  };

  const getUserAvatarInitial = (role) => {
    switch (role) {
      case USER_ROLES.SUPER_ADMIN:
        return "S";
      case USER_ROLES.CRO:
      case USER_ROLES.CRO_COORDINATOR:
        return "K";
      case USER_ROLES.DATA_MANAGER:
        return "DM";
      case USER_ROLES.INVESTIGATOR:
        return "I";
      case USER_ROLES.CRC:
        return "C";
      case USER_ROLES.REVIEWER:
        return "R";
      case USER_ROLES.SUB_INVESTIGATOR:
        return "SI";
      default:
        return user_data.email?.substring(0, 1).toUpperCase();
    }
  };

  return (
    <>
      <ImpersonationBanner />
      <ViewDashboardAsModal open={viewAsOpen} onClose={() => setViewAsOpen(false)} />
      <Box
        sx={{
          px: 0,
          py: 3,
          borderBottom: "2px solid #eee",
          backgroundColor: "#ffffff",
        }}
      >
        <Stack direction="row" spacing={3} justifyContent="space-between" alignItems="center">
          {/* Logo */}
          <Link to="/">
            <Box component="img" src={logo} alt="Logo" sx={{ height: 40 }} />
          </Link>

          {/* Nav Links */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            {user_data.role === USER_ROLES.PATIENT && (
              <NavItem
                to="/"
                active={active === "home"}
                icon={<HomeIcon />}
                label="Home"
                onClick={() => logUserAction("Click to Home Page")}
              />
            )}

            {user_data.role !== USER_ROLES.PATIENT &&
              user_data.role !== USER_ROLES.REVIEWER &&
              user_data.role !== USER_ROLES.CRO &&
              user_data.role !== USER_ROLES.CRO_COORDINATOR &&
              user_data.role !== USER_ROLES.DATA_MANAGER &&
              user_data.role !== USER_ROLES.CRC && (
                <NavItem
                  to={
                    user_data.role !== USER_ROLES.INVESTIGATOR &&
                    user_data.role !== USER_ROLES.SUB_INVESTIGATOR &&
                    user_data.role !== USER_ROLES.SUPER_ADMIN
                      ? "/"
                      : "/calendar/"
                  }
                  active={active === "calender"}
                  icon={<CalendarIcon />}
                  label="Calendar"
                  onClick={() => logUserAction("Calendar Button clicked")}
                />
              )}

            {user_data.role !== USER_ROLES.PATIENT &&
              user_data.role !== USER_ROLES.CRO &&
              user_data.role !== USER_ROLES.CRO_COORDINATOR && (
                <NavItem
                  to="/patient/"
                  active={active === "patient"}
                  icon={<PatientIcon />}
                  label="Subjects"
                  onClick={() => logUserAction("Subject Button clicked", "-")}
                />
              )}

            {user_data.role !== USER_ROLES.PATIENT && (
              <NavItem
                to="/clinic_trials/"
                active={active === "trials"}
                icon={<TrialsIcon />}
                label="Trials"
                onClick={() => logUserAction("Click on Trials Button")}
              />
            )}

            {user_data.role !== USER_ROLES.PATIENT && user_data.role !== USER_ROLES.REVIEWER && (
              <>
                {(user_data.role === USER_ROLES.SUPER_ADMIN ||
                  user_data.role === USER_ROLES.CLINIC_OWNER ||
                  user_data.role === USER_ROLES.INVESTIGATOR ||
                  user_data.role === USER_ROLES.SUB_INVESTIGATOR) && (
                  <NavItem
                    to="/clinic/"
                    active={active === "clinic"}
                    icon={<ClinicIcon />}
                    label="Clinics"
                    onClick={() => logUserAction("Click on Clinic Button")}
                  />
                )}

                {(user_data.role === USER_ROLES.REVIEWER ||
                  ![
                    USER_ROLES.PRACTITIONER,
                    USER_ROLES.CRO_COORDINATOR,
                    USER_ROLES.DATA_MANAGER,
                    USER_ROLES.CRC,
                    USER_ROLES.SUB_INVESTIGATOR,
                  ].includes(user_data.role)) && (
                  <NavItem
                    to="/staff/"
                    active={active === "staff"}
                    icon={<StaffIcon />}
                    label="Staff"
                    onClick={() => {
                      logUserAction("Click on Staff Button");
                      handleStaffClick();
                    }}
                  />
                )}

                {user_data.role !== USER_ROLES.CRC &&
                  user_data.role !== USER_ROLES.CRO &&
                  user_data.role !== USER_ROLES.CRO_COORDINATOR && (
                    <NavItem
                      to="/patient/adhoc-report"
                      active={active === "adhoc-report"}
                      icon={<ReportGenerationIcon />}
                      label="Report Generation"
                      onClick={() => logUserAction("Click On Report Generation Button")}
                    />
                  )}
              </>
            )}

            {user_data.role === USER_ROLES.SUPER_ADMIN && (
              <NavItem
                to="/patient/user-action-logs"
                active={active === "user-action-logs"}
                icon={<HistoryIcon />}
                label="User Activity Log"
                onClick={() => logUserAction("Click on User Action Logs Button")}
              />
            )}

            {(user_data.role === USER_ROLES.SUPER_ADMIN || user_data.role === USER_ROLES.CRO) && (
              <NavItem
                to="/survey-poem"
                active={active === "survey"}
                icon={<SurveyIcon />}
                label="POEMs"
                onClick={() => logUserAction("Click on POEMs Button")}
              />
            )}

            {(user_data.role === USER_ROLES.SUPER_ADMIN ||
              user_data.role === USER_ROLES.CRO ||
              user_data.role === USER_ROLES.CRO_COORDINATOR) && (
              <NavItem
                to="/form-creater-v2-list"
                active={active === "form_creater"}
                icon={<SourceIcon />}
                label="Source Doc Creator"
                onClick={() => logUserAction("Click on Source Doc Creator Button")}
              />
            )}

            {(user_data.role === USER_ROLES.SUPER_ADMIN || user_data.role === USER_ROLES.CRO) && (
              <NavItem
                to="/advanced-surveys"
                active={active === "advanced_surveys"}
                icon={<SurveyIcon />}
                label="ePRO"
                onClick={() => logUserAction("Click on ePRO Button")}
              />
            )}

            {(user_data.role === USER_ROLES.SUPER_ADMIN ||
              INV_RELATED_ROLES.includes(user_data.role)) && (
              <NavItem
                to="/support"
                active={active === "support"}
                icon={<SupportIcon />}
                label="Support"
                badge={supportUnreadCount}
                onClick={() => logUserAction("Click on Support Button")}
              />
            )}
          </Stack>

          <Stack direction={"row"} alignItems={"center"}>
            {user_data.role === USER_ROLES.SUPER_ADMIN && false && (
              <Tooltip title="View Dashboard As">
                <IconButton onClick={() => setViewAsOpen(true)} size="small">
                  <ViewAsIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Notifications Bell */}
            <Box>
              <Tooltip title="Notifications">
                <IconButton onClick={handleNotifOpen} size="small">
                  <Badge badgeContent={unreadCount} color="error" max={99}>
                    <NotificationIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Popover
                open={notifOpen}
                anchorEl={notifAnchorEl}
                onClose={handleNotifClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: { width: 380, maxHeight: 480 } }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 2, py: 1.5, borderBottom: "1px solid #eee" }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Notifications
                  </Typography>
                  <Button
                    size="small"
                    disabled={unreadCount === 0 || markingAll}
                    onClick={() => markAllRead()}
                  >
                    Mark all read
                  </Button>
                </Stack>

                {notifLoading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : notifications.length === 0 ? (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No notifications
                  </Typography>
                ) : (
                  <List disablePadding sx={{ overflowY: "auto", maxHeight: 360 }}>
                    {notifications.map((n) => (
                      <ListItem
                        key={n.id}
                        button
                        alignItems="flex-start"
                        onClick={() => handleNotificationClick(n)}
                        sx={{
                          backgroundColor: n.is_read ? "transparent" : "#fff5f7",
                          borderBottom: "1px solid #f0f0f0",
                          cursor: "pointer",
                          "&:hover": { backgroundColor: "#fce4ec" },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={n.is_read ? "normal" : "bold"}>
                              {n.title}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={0.5}>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {n.message}
                              </Typography>
                              {n.site_paticipant_id && (
                                <Typography variant="caption" color="primary">
                                  Patient: {n.site_paticipant_id}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.disabled">
                                {dayjs.utc(n.created_at).local().fromNow()}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Stack sx={{ borderTop: "1px solid #eee", p: 1 }}>
                  <Button
                    size="small"
                    fullWidth
                    component={Link}
                    to="/notifications"
                    onClick={handleNotifClose}
                  >
                    View all notifications
                  </Button>
                </Stack>
              </Popover>
            </Box>

            {/* Avatar */}
            <Box>
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{ ml: 1 }}
                  aria-controls={open ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                >
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {getUserAvatarInitial(user_data.role)}
                  </Avatar>
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    mt: 1.5,
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.12))",
                    "&:before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem>
                  <b>{user_data.email}</b>
                </MenuItem>
                <MenuItem>
                  <small>[{user_data.role}]</small>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => user.actions.logout()}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </>
  );
});

// Reusable NavItem Component
const NavItem = ({ to, active, icon, label, badge, onClick }) => (
  <Link to={to} onClick={onClick} style={{ textDecoration: "none" }}>
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        color: active ? "#EE295D" : "black",
        fontWeight: active ? "bold" : "normal",
        borderBottom: active ? "2px solid" : "none",
        py: 0.5,
        px: 0,
        "&:hover": {
          color: "#EE295D",
        },
      }}
    >
      <Badge badgeContent={badge || 0} color="error" invisible={!badge}>
        {icon}
      </Badge>
      <Typography variant="body1">{label}</Typography>
    </Stack>
  </Link>
);

export default NavBar;
